/**
 * Local SQLite attach flow — the composition step that runs when the
 * user picks a file via `showOpenFilePicker`. v1.2 reshapes the flow
 * around OPFS: instead of hashing the file twice (once for SHA-256,
 * once for `sqlite3_deserialize`), a single worker round-trip streams
 * the bytes into OPFS while computing the hash, then the server is
 * called with the already-known hash + size, and the IDB record is
 * stamped with the fresh `opfsName`.
 *
 * Steps:
 *  1. Capability check (T9) — refuse with `unsupported-browser` if
 *     the runtime can't host an OPFS-backed SQLite engine.
 *  2. Quota check (T6) — refuse with `blocked-too-large` if the file
 *     wouldn't fit in remaining storage budget with headroom.
 *  3. Worker call: stream-copy into OPFS + SHA-256 in one pass.
 *     Returns `{ opfsName, sha256, fileSize, evicted }`.
 *  4. Server call: `POST /api/saved-connections/local-sqlite` with
 *     the hash + size.
 *  5. IDB write: persist the handle record keyed by the server-issued
 *     `saved_connection_id`, including the fresh `opfsName`.
 *  6. LRU eviction bookkeeping: for each evicted opfsName from the
 *     worker reply, null out the `opfsName` field on the
 *     corresponding IDB record (the connector card still appears;
 *     next open will re-copy).
 *
 * Returns a discriminated union the connector card branches on:
 * - `{ kind: "attached", savedConnection, record }`
 * - `{ kind: "unsupported-browser", reason }`
 * - `{ kind: "blocked-too-large", reason }`
 * - `{ kind: "user-cancelled" }`
 *
 * Pure-orchestrator surface: every IO call is injected, so tests
 * exercise the same function without touching the FSA, the worker,
 * IDB, or the network. Production wires the defaults at the
 * component level.
 */
import {
  checkSqliteAttachQuota,
  type SqliteAttachQuotaResult,
} from "Worker/sqlite";
import { checkOpfsCapable, type OpfsCapability } from "./featureDetect";
import {
  DuplicateLocalSqliteError,
  type CreateLocalSqliteSavedConnectionRequest,
  type SavedConnection,
} from "components/canvas/leftSide/import/Postgres/savedConnectionsApi";
import {
  getSqliteHandle,
  listAllSqliteHandles,
  putSqliteHandle,
  setOpfsNameForHandle,
  type SqliteHandleRecord,
} from "stores/sqliteHandles";

export interface AttachFlowDeps {
  /** Stream the source `file` into OPFS while hashing it. Production
   *  wires this to the worker's `SqliteAttachToOpfs` task. */
  attachToOpfs: (
    file: File,
    handle: FileSystemFileHandle,
    signal: AbortSignal | undefined,
  ) => Promise<{
    opfsName: string;
    sha256: string;
    fileSize: number;
    evicted: string[];
  }>;
  /** Server call — production wires this to
   *  `createLocalSqliteSavedConnection`. */
  createConnection: (
    body: CreateLocalSqliteSavedConnectionRequest,
  ) => Promise<SavedConnection>;
  /** Optional override for the capability check. Defaults to
   *  [[checkOpfsCapable]]. */
  capabilityCheck?: () => OpfsCapability;
  /** Optional override for the quota check. Defaults to
   *  [[checkSqliteAttachQuota]]. */
  quotaCheck?: (fileSize: number) => Promise<SqliteAttachQuotaResult>;
  /** Side-effecting persistence of the handle record after the
   *  server confirms the row. Defaults to [[putSqliteHandle]]. */
  persistHandle?: (
    record: Omit<SqliteHandleRecord, "lastAttachedAt">,
  ) => Promise<void>;
  /** Side-effecting LRU eviction bookkeeping: null out the cached
   *  opfsName for each evicted record. Defaults to
   *  [[setOpfsNameForHandle]] applied per-record (the caller has the
   *  opfsName, not the savedConnectionId — production resolves the
   *  reverse lookup via [[listAllSqliteHandles]]). */
  recordEviction?: (evictedOpfsName: string) => Promise<void>;
  /** Drop a freshly-written OPFS entry when the server rejects the
   *  attach (duplicate-hash 409). The bytes are otherwise orphaned
   *  until the LRU eviction path catches them on a subsequent
   *  attach. Production wires this to the worker's `unlinkOpfsFile`
   *  task. Default is a no-op so unit tests don't need a worker
   *  bridge; the orphan is purely a space-pressure concern, not a
   *  correctness one. */
  unlinkOpfs?: (opfsName: string) => Promise<void>;
}

export type AttachFlowResult =
  | {
      kind: "attached";
      savedConnection: SavedConnection;
      record: SqliteHandleRecord;
    }
  | {
      kind: "unsupported-browser";
      reason: string;
    }
  | {
      kind: "blocked-too-large";
      fileSize: number;
      remainingBytes: number;
      reason: string;
    }
  | { kind: "user-cancelled" }
  | {
      /** Server detected this user already has a connection for the
       *  same SHA-256 hash. Surfaces both pieces the UI needs to
       *  render an inline error with a jump-to-existing button. The
       *  freshly-written OPFS bytes have been cleaned up before this
       *  branch returns. */
      kind: "duplicate";
      existingConnectionId: string;
      existingDisplayName: string;
    };

export interface AttachFlowInput {
  /** Display name the user typed (or the filename minus extension).
   *  Server validates non-empty + length. */
  displayName: string;
  /** The handle returned by `showOpenFilePicker`. The attach flow
   *  itself does NOT call the picker — the React component does
   *  (a user-gesture requirement of the FSA API). */
  handle: FileSystemFileHandle;
  /** Optional cancellation handle. Surfaced through the worker
   *  attach step; if the user clicks "Cancel" mid-copy, the function
   *  returns `kind: "user-cancelled"` rather than throwing. */
  abortSignal?: AbortSignal;
}

/**
 * Run the attach flow. See module doc-string for the step ordering.
 *
 * Returns the discriminated `AttachFlowResult` so callers don't need
 * try/catch for the expected branches; uncaught throws indicate a
 * real failure (server 500, IDB write rejected, etc.).
 */
export async function attachLocalSqlite(
  input: AttachFlowInput,
  deps: AttachFlowDeps,
): Promise<AttachFlowResult> {
  const { displayName, handle, abortSignal } = input;
  const {
    attachToOpfs,
    createConnection,
    capabilityCheck = checkOpfsCapable,
    quotaCheck = checkSqliteAttachQuota,
    persistHandle = (record) => putSqliteHandle(record),
    recordEviction = (opfsName) =>
      setOpfsNameForHandleByOpfsName(opfsName, null),
    unlinkOpfs = async (_opfsName: string) => {
      // Default: no-op. Production wires this to the worker's
      // unlinkOpfsFile task. The orphan bytes are recovered by the
      // LRU eviction path on a subsequent attach.
    },
  } = deps;

  abortSignal?.throwIfAborted();

  const capability = capabilityCheck();
  if (!capability.ok) {
    return { kind: "unsupported-browser", reason: capability.message };
  }

  const file = await handle.getFile();

  const quota = await quotaCheck(file.size);
  if (quota.kind === "block") {
    return {
      kind: "blocked-too-large",
      fileSize: quota.fileSize,
      remainingBytes: quota.remainingBytes,
      reason: quota.reason,
    };
  }

  let opfsName: string;
  let sha256Hash: string;
  let fileSize: number;
  let evicted: string[];
  try {
    const result = await attachToOpfs(file, handle, abortSignal);
    opfsName = result.opfsName;
    sha256Hash = result.sha256;
    fileSize = result.fileSize;
    evicted = result.evicted;
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      return { kind: "user-cancelled" };
    }
    throw e;
  }

  let savedConnection: SavedConnection;
  try {
    savedConnection = await createConnection({
      display_name: displayName,
      sha256_hash: sha256Hash,
      file_size: fileSize,
    });
  } catch (e) {
    if (e instanceof DuplicateLocalSqliteError) {
      // Server detected an existing connection with the same hash.
      // The OPFS bytes we just wrote are orphans — best-effort
      // cleanup so we don't leak storage on every duplicate attempt.
      await unlinkOpfs(opfsName).catch(() => undefined);
      return {
        kind: "duplicate",
        existingConnectionId: e.existingConnectionId,
        existingDisplayName: e.existingDisplayName,
      };
    }
    throw e;
  }

  const record: SqliteHandleRecord = {
    savedConnectionId: savedConnection.id,
    handle,
    sha256Hash,
    fileSize,
    lastModified: file.lastModified,
    lastAttachedAt: Date.now(),
    opfsName,
    fileName: handle.name,
  };
  await persistHandle(record);

  // LRU eviction bookkeeping. Best-effort; failures here don't roll
  // back the attach — the connector is already valid and the worst
  // case is an IDB row claiming a cached copy that doesn't exist,
  // which the next open's cache-miss path handles correctly.
  for (const evictedOpfsName of evicted) {
    await recordEviction(evictedOpfsName).catch(() => undefined);
  }

  return { kind: "attached", savedConnection, record };
}

// ----------------------------------------------------------------------------
// Reattach flow — for an EXISTING saved connection that's not currently
// usable from this browser (handle missing, permission denied, file
// moved on disk, etc.). The connection row stays put; we just refresh
// the per-device IDB handle + OPFS cache. The server is not called.
// ----------------------------------------------------------------------------

export interface ReattachFlowInput {
  /** The existing server-side connection. Source of truth for the
   *  expected SHA-256 + file size — the picked file is verified
   *  against these before the IDB record is overwritten. */
  savedConnection: SavedConnection;
  /** Fresh handle from `showOpenFilePicker` on the modal's gesture. */
  handle: FileSystemFileHandle;
  abortSignal?: AbortSignal;
}

export type ReattachFlowResult =
  | { kind: "reattached"; record: SqliteHandleRecord }
  | { kind: "unsupported-browser"; reason: string }
  | {
      kind: "blocked-too-large";
      fileSize: number;
      remainingBytes: number;
      reason: string;
    }
  | { kind: "user-cancelled" }
  | {
      /** Picked file's SHA-256 doesn't match the connection's stored
       *  hash. Surfaces both so the UI can say "you picked X but this
       *  connection wants Y" without leaking the hashes into prose.
       *  The freshly-written OPFS bytes are unlinked before we return. */
      kind: "hash-mismatch";
      pickedSha: string;
      expectedSha: string;
    };

/** Same shape as `AttachFlowDeps` minus `createConnection` (no server
 *  call on reattach) and plus nothing — re-uses every other injection
 *  point so production wiring stays one-shot. */
export type ReattachFlowDeps = Omit<AttachFlowDeps, "createConnection">;

/**
 * Re-attach an existing saved connection to a freshly-picked file.
 *
 * Flow:
 *  1. Capability + quota gates (same as attach).
 *  2. Stream the file into OPFS while hashing (same worker call).
 *  3. Verify the resulting hash matches the connection's stored
 *     `sha256_hash`. Mismatch → unlink the just-written OPFS bytes,
 *     return `hash-mismatch`. The old IDB record is left alone — the
 *     user can pick a different file and try again.
 *  4. Match → unlink the PREVIOUS opfsName (if any) and overwrite the
 *     IDB record with the new handle + opfsName + fileName.
 *
 * Side-step: file_size is part of the saved connection too, but we
 * don't gate on it pre-verification — the SHA already proves byte
 * equality. A size mismatch implies a hash mismatch.
 */
export async function reattachLocalSqlite(
  input: ReattachFlowInput,
  deps: ReattachFlowDeps,
): Promise<ReattachFlowResult> {
  const { savedConnection, handle, abortSignal } = input;
  if (savedConnection.credentials.type !== "local_sqlite") {
    throw new Error(
      "reattachLocalSqlite called with a non-local-sqlite connection",
    );
  }
  const expectedSha = savedConnection.credentials.sha256_hash;
  const {
    attachToOpfs,
    capabilityCheck = checkOpfsCapable,
    quotaCheck = checkSqliteAttachQuota,
    persistHandle = (record) => putSqliteHandle(record),
    recordEviction = (opfsName) =>
      setOpfsNameForHandleByOpfsName(opfsName, null),
    unlinkOpfs = async (_opfsName: string) => {
      // Default: no-op. Production wires this to the worker's
      // unlinkOpfsFile task. See note in `attachLocalSqlite`.
    },
  } = deps;

  abortSignal?.throwIfAborted();

  const capability = capabilityCheck();
  if (!capability.ok) {
    return { kind: "unsupported-browser", reason: capability.message };
  }

  const file = await handle.getFile();

  const quota = await quotaCheck(file.size);
  if (quota.kind === "block") {
    return {
      kind: "blocked-too-large",
      fileSize: quota.fileSize,
      remainingBytes: quota.remainingBytes,
      reason: quota.reason,
    };
  }

  let opfsName: string;
  let sha256Hash: string;
  let fileSize: number;
  let evicted: string[];
  try {
    const result = await attachToOpfs(file, handle, abortSignal);
    opfsName = result.opfsName;
    sha256Hash = result.sha256;
    fileSize = result.fileSize;
    evicted = result.evicted;
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      return { kind: "user-cancelled" };
    }
    throw e;
  }

  if (sha256Hash !== expectedSha) {
    // Wrong file. Clean up the OPFS bytes we just wrote so we don't
    // leak a cached copy that maps to nothing.
    await unlinkOpfs(opfsName).catch(() => undefined);
    return {
      kind: "hash-mismatch",
      pickedSha: sha256Hash,
      expectedSha,
    };
  }

  // Same file, different handle. Drop the previous OPFS bytes (if any)
  // so we don't leave two cached copies of the same content under this
  // savedConnectionId. Best-effort: a failure here just leaves the old
  // copy until LRU eviction reclaims it.
  const previous = await getSqliteHandle(savedConnection.id).catch(
    () => undefined,
  );
  if (previous?.opfsName && previous.opfsName !== opfsName) {
    await unlinkOpfs(previous.opfsName).catch(() => undefined);
  }

  const record: SqliteHandleRecord = {
    savedConnectionId: savedConnection.id,
    handle,
    sha256Hash,
    fileSize,
    lastModified: file.lastModified,
    lastAttachedAt: Date.now(),
    opfsName,
    fileName: handle.name,
  };
  await persistHandle(record);

  for (const evictedOpfsName of evicted) {
    await recordEviction(evictedOpfsName).catch(() => undefined);
  }

  return { kind: "reattached", record };
}

/** Default `recordEviction` impl: look up the IDB record by
 *  `opfsName` and null out its cache pointer. Lives here rather than
 *  in `stores/sqliteHandles.ts` so the store stays agnostic about
 *  reverse-lookups (a savedConnectionId-keyed store doesn't owe its
 *  callers an opfsName index). */
async function setOpfsNameForHandleByOpfsName(
  evictedOpfsName: string,
  newOpfsName: string | null,
): Promise<void> {
  const all = await listAllSqliteHandles();
  const match = all.find((r) => r.opfsName === evictedOpfsName);
  if (match) {
    await setOpfsNameForHandle(match.savedConnectionId, newOpfsName);
  }
}
